import type { ColumnDef } from '@tanstack/react-table';
import { Checkbox } from '@/components/ui/checkbox';
import type { PcmpRowSelection } from '../../hooks/usePcmpRowSelection';
import type { PcmpQueueItem } from '../../types/pcmpQueue.types';

interface PcmpSelectColumnOptions<T extends PcmpQueueItem> {
  selection: PcmpRowSelection;
  /** Rows that cannot be picked show a muted box with this reason. */
  blockedReason?: (item: T) => string | null;
}

// The row itself opens the proposal; keep checkbox clicks from reaching it.
const stop = (event: { stopPropagation: () => void }) => event.stopPropagation();

/** A checkbox column; the header ticks the page's selectable rows. */
export function pcmpSelectColumn<T extends PcmpQueueItem>({
  selection,
  blockedReason,
}: PcmpSelectColumnOptions<T>): ColumnDef<T> {
  const selectable = (item: T) => !blockedReason?.(item);

  return {
    id: 'select',
    header: ({ table }) => {
      const rows = table
        .getRowModel()
        .rows.map((row) => row.original)
        .filter(selectable);
      const picked = rows.filter((item) => selection.isSelected(item.proposalId)).length;
      return (
        <Checkbox
          aria-label="Select this page"
          checked={rows.length > 0 && picked === rows.length}
          indeterminate={picked > 0 && picked < rows.length}
          disabled={rows.length === 0}
          onCheckedChange={(checked) => selection.setRows(rows, checked)}
        />
      );
    },
    cell: ({ row }) => {
      const item = row.original;
      const reason = blockedReason?.(item) ?? null;
      return (
        <div onClick={stop} onKeyDown={stop} role="presentation" title={reason ?? undefined}>
          <Checkbox
            aria-label={`Select ${item.refNo}`}
            checked={!reason && selection.isSelected(item.proposalId)}
            disabled={reason !== null}
            onCheckedChange={() => selection.toggle(item)}
          />
        </div>
      );
    },
  };
}
